import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Grid,
    IconButton,
    Pagination,
    Alert,
    Tooltip
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import styled from '@emotion/styled';
import { useDispatch, useSelector } from 'react-redux';
import { getClientBillingOverview } from '../redux/ducks/invoices';
import { useHasPermission } from '../utils/permissionUtils';
import PageHeader from '../components/PageHeader';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { statusSeverity } from '../utils/statusColors';
import { formatDayShort } from '../utils/dateUtils';

const BackTextButton = styled(Button)(({ theme }) => ({
    flexShrink: 0,
    [theme.breakpoints.down('sm')]: {
        display: 'none',
    },
}));

const BackIconButton = styled(IconButton)(({ theme }) => ({
    flexShrink: 0,
    display: 'none',
    [theme.breakpoints.down('sm')]: {
        display: 'inline-flex',
    },
}));

const StatsGrid = styled(Grid)(({ theme }) => ({
    marginBottom: theme.spacing(3),
}));

const ClickableTableRow = styled(TableRow)({
    cursor: 'pointer',
});

const ContainerBox = styled(Box)(({ theme }) => ({
    width: '100%',
    minWidth: 0,
    maxWidth: theme.breakpoints.values.xl,
    margin: '0 auto',
    padding: theme.spacing(2),
    overflowX: 'clip',
    [theme.breakpoints.up('md')]: {
        padding: theme.spacing(3),
    },
}));

const SkeletonHeader = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(3),
}));

const SkeletonStatGrid = styled(Grid)(({ theme }) => ({
    marginBottom: theme.spacing(3),
}));

const SkeletonSection = styled(Box)(({ theme }) => ({
    marginTop: theme.spacing(3),
}));

const PaginationRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    marginTop: theme.spacing(2),
}));

const LoadFailureAlert = styled(Alert)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

const ErrorBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(2),
}));

const TableFootnote = styled(Typography)(({ theme }) => ({
    display: 'block',
    marginTop: theme.spacing(1),
}));

const INVOICES_PER_PAGE = 50;

const ABSORBED_HOURS_EXPLAINER = 'Hours that were worked and delivered beyond the retainer, but never charged because this retainer does not bill for overage.';

const StatCard = ({ label, value, sub }) => (
    <Card variant="outlined">
        <CardContent>
            <Typography variant="caption" color="text.secondary">{label}</Typography>
            <Typography variant="h5" fontWeight={600}>{value}</Typography>
            {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
        </CardContent>
    </Card>
);

const formatCurrency = (n) => `$${(Number(n) || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
const formatHours = (n) => `${(Number(n) || 0).toFixed(2)} h`;
const formatDate = (d) => formatDayShort(d);

const statusColor = (status) => statusSeverity('invoice', status);

export default function ClientBillingOverview() {
    const { clientId: routeClientId } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const hasPermission = useHasPermission();
    const isInternal = hasPermission('canSeeInternal');
    const ownClientId = useSelector(state => {
        const c = state.auth.currentUser?.client;
        return c?._id || c || null;
    });
    const clientId = routeClientId || ownClientId;
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [page, setPage] = useState(1);
    const [loadedPage, setLoadedPage] = useState(1);
    const [failedPage, setFailedPage] = useState(null);
    const [retryToken, setRetryToken] = useState(0);
    const [invoiceTotal, setInvoiceTotal] = useState(0);
    const invoicePageCount = Math.max(1, Math.ceil(invoiceTotal / INVOICES_PER_PAGE));

    useEffect(() => {
        setPage(1);
        setFailedPage(null);
    }, [clientId]);

    useEffect(() => {
        if (page > invoicePageCount) setPage(invoicePageCount);
    }, [page, invoicePageCount]);

    useEffect(() => {
        if (!clientId) {
            setLoading(false);
            return;
        }
        let active = true;
        const requestedPage = page;
        setLoading(true);
        dispatch(getClientBillingOverview(clientId, (requestedPage - 1) * INVOICES_PER_PAGE, INVOICES_PER_PAGE, (result) => {
            if (!active) return;
            setLoading(false);
            if (!result) {
                setFailedPage(requestedPage);
                return;
            }
            setFailedPage(null);
            setData(result);
            setLoadedPage(requestedPage);
            setInvoiceTotal(Number.isFinite(result.total) ? result.total : (result.invoices || []).length);
        }));
        return () => { active = false; };
    }, [clientId, dispatch, page, retryToken]);

    const retryLoad = () => setRetryToken((prev) => prev + 1);

    const invoiceLinkPath = (id) => isInternal
        ? `/billing/invoices/${id}`
        : `/client/${data?.client?.slug || clientId}/invoices/${id}`;

    if (loading && !data) {
        return (
            <ContainerBox>
                <SkeletonHeader>
                    <Skeleton height={36} width="40%" />
                    <Skeleton height={20} width="60%" />
                </SkeletonHeader>
                <SkeletonStatGrid container spacing={2}>
                    {[0, 1, 2, 3].map((i) => (
                        <Grid item xs={6} md={3} key={i}>
                            <Skeleton height={92} />
                        </Grid>
                    ))}
                </SkeletonStatGrid>
                <Skeleton height={28} width="30%" />
                <Skeleton height={220} />
                <SkeletonSection>
                    <Skeleton height={28} width="30%" />
                    <Skeleton height={220} />
                </SkeletonSection>
            </ContainerBox>
        );
    }

    if (!data) {
        return (
            <ContainerBox>
                <ErrorBox>
                    <Typography>Unable to load client overview.</Typography>
                    <Button variant="outlined" onClick={retryLoad}>Try again</Button>
                </ErrorBox>
            </ContainerBox>
        );
    }

    const { client, period, byBillingType, totals, retainer, fixedPriceProjects, invoices, invoiceStats } = data;
    const firstShownInvoice = invoices.length ? (loadedPage - 1) * INVOICES_PER_PAGE + 1 : 0;
    const lastShownInvoice = (loadedPage - 1) * INVOICES_PER_PAGE + invoices.length;
    const showAbsorbedHours = isInternal && (retainer?.history || []).some((h) => h.absorbedHours !== undefined && h.absorbedHours !== null);

    return (
        <ContainerBox>
            <PageHeader
                title={isInternal ? (client?.name || '') : 'My Billing'}
                subtitle={
                    <>
                        Billing Overview · {formatDate(period.start)} – {formatDate(period.end)}
                        {client.billingTerms?.type && ` · ${client.billingTerms.type}`}
                    </>
                }
                actions={isInternal ? (
                    <>
                        <BackTextButton startIcon={<ArrowBack />} onClick={() => navigate('/billing/clients')}>
                            Back to Clients
                        </BackTextButton>
                        <BackIconButton
                            onClick={() => navigate('/billing/clients')}
                            aria-label="Back to clients"
                        >
                            <ArrowBack />
                        </BackIconButton>
                    </>
                ) : null}
            />

            <StatsGrid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard label="Invoiced (12 mo)" value={formatCurrency(totals.invoicedAmount)} sub={`${invoiceStats.total} invoices`} />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard label="Paid" value={formatCurrency(totals.paidAmount)} />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard label="Outstanding" value={formatCurrency(totals.outstandingAmount)} />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                        label="Avg Hourly Spend"
                        value={formatCurrency(totals.averageHourlyRate)}
                        sub={`${formatHours(totals.billableHours)} billable`}
                    />
                </Grid>
            </StatsGrid>

            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <Card>
                        <CardHeader title="Time by Billing Type" subheader="Last 12 months" />
                        <CardContent>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Type</TableCell>
                                            <TableCell align="right">Hours</TableCell>
                                            <TableCell align="right">Invoiced</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {Object.entries(byBillingType).map(([type, stats]) => (
                                            <TableRow key={type}>
                                                <TableCell>{type}</TableCell>
                                                <TableCell align="right">{formatHours(stats.hours)}</TableCell>
                                                <TableCell align="right">{formatCurrency(stats.invoicedAmount)}</TableCell>
                                            </TableRow>
                                        ))}
                                        <TableRow>
                                            <TableCell><strong>Total</strong></TableCell>
                                            <TableCell align="right"><strong>{formatHours(totals.totalHours)}</strong></TableCell>
                                            <TableCell align="right"><strong>{formatCurrency(totals.invoicedAmount)}</strong></TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Card>
                        <CardHeader title="Invoice Status Summary" />
                        <CardContent>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Status</TableCell>
                                            <TableCell align="right">Count</TableCell>
                                            <TableCell align="right">Amount</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {['Draft', 'Open', 'Paid', 'Overdue'].map(status => (
                                            <TableRow key={status}>
                                                <TableCell><Chip label={status} color={statusColor(status)} size="small" /></TableCell>
                                                <TableCell align="right">{invoiceStats.byStatus[status] || 0}</TableCell>
                                                <TableCell align="right">{formatCurrency(invoiceStats.byStatusAmount[status] || 0)}</TableCell>
                                            </TableRow>
                                        ))}
                                        <TableRow>
                                            <TableCell>Cancelled</TableCell>
                                            <TableCell align="right">{invoiceStats.byStatus.Cancelled || 0}</TableCell>
                                            <TableCell align="right">—</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell><strong>Avg invoice</strong></TableCell>
                                            <TableCell align="right" colSpan={2}><strong>{formatCurrency(invoiceStats.avgInvoiceAmount)}</strong></TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </CardContent>
                    </Card>
                </Grid>

                {retainer && (
                    <Grid item xs={12}>
                        <Card>
                            <CardHeader
                                title="Retainer History"
                                subheader={`${formatHours(retainer.hoursIncluded)} included · ${formatCurrency(retainer.baseCharge)} base${retainer.frequency ? ` · ${retainer.frequency}` : ''}`}
                            />
                            <CardContent>
                                {retainer.history.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary">No retainer invoices in the last 12 months.</Typography>
                                ) : (
                                    <TableContainer>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Invoice</TableCell>
                                                    <TableCell>Period</TableCell>
                                                    <TableCell align="right">Base Charge</TableCell>
                                                    <TableCell align="right">
                                                        <Tooltip title="Hours in this period that the retainer charge paid for, up to the hours the retainer includes.">
                                                            <span>Hours Used</span>
                                                        </Tooltip>
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Tooltip title="Hours worked beyond the retainer that were billed separately.">
                                                            <span>Overage Hours</span>
                                                        </Tooltip>
                                                    </TableCell>
                                                    {showAbsorbedHours && (
                                                        <TableCell align="right">
                                                            <Tooltip title={ABSORBED_HOURS_EXPLAINER}>
                                                                <span>Absorbed Hours (Not Billed)</span>
                                                            </Tooltip>
                                                        </TableCell>
                                                    )}
                                                    <TableCell align="right">Overage Charge</TableCell>
                                                    <TableCell>Status</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {retainer.history.map(h => (
                                                    <ClickableTableRow key={h.invoiceId} hover onClick={() => navigate(invoiceLinkPath(h.invoiceId))}>
                                                        <TableCell>{h.invoiceNumber}</TableCell>
                                                        <TableCell>{formatDate(h.periodStart)} – {formatDate(h.periodEnd)}</TableCell>
                                                        <TableCell align="right">{formatCurrency(h.baseCharge)}</TableCell>
                                                        <TableCell align="right">{formatHours(h.hoursIncluded)}</TableCell>
                                                        <TableCell align="right">{formatHours(h.overageHours)}</TableCell>
                                                        {showAbsorbedHours && (
                                                            <TableCell align="right">
                                                                {h.absorbedHours === undefined || h.absorbedHours === null ? '-' : formatHours(h.absorbedHours)}
                                                            </TableCell>
                                                        )}
                                                        <TableCell align="right">{formatCurrency(h.overageAmount)}</TableCell>
                                                        <TableCell><Chip label={h.status} color={statusColor(h.status)} size="small" /></TableCell>
                                                    </ClickableTableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                )}
                                {showAbsorbedHours && retainer.history.length > 0 && (
                                    <TableFootnote variant="caption" color="text.secondary">
                                        Absorbed Hours: {ABSORBED_HOURS_EXPLAINER} Staff only, never shown to the client.
                                    </TableFootnote>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                )}

                {fixedPriceProjects.length > 0 && (
                    <Grid item xs={12}>
                        <Card>
                            <CardHeader title="Fixed Price Projects" />
                            <CardContent>
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Project</TableCell>
                                                <TableCell>Account</TableCell>
                                                <TableCell align="right">Contract Value</TableCell>
                                                <TableCell align="right">Paid</TableCell>
                                                <TableCell align="right">Invoiced</TableCell>
                                                <TableCell align="right">Due</TableCell>
                                                <TableCell align="right">Payments</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {fixedPriceProjects.map(p => (
                                                <TableRow key={p.projectId}>
                                                    <TableCell>{p.projectName}</TableCell>
                                                    <TableCell>{p.accountName}</TableCell>
                                                    <TableCell align="right">{formatCurrency(p.totalValue)}</TableCell>
                                                    <TableCell align="right">{formatCurrency(p.paidValue)}</TableCell>
                                                    <TableCell align="right">{formatCurrency(p.billedValue)}</TableCell>
                                                    <TableCell align="right">{formatCurrency(p.dueValue)}</TableCell>
                                                    <TableCell align="right">{p.paymentsPaid} / {p.paymentsCount}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </CardContent>
                        </Card>
                    </Grid>
                )}

                <Grid item xs={12}>
                    <Card>
                        <CardHeader
                            title="Invoices"
                            subheader={invoicePageCount > 1
                                ? `Showing ${firstShownInvoice}–${lastShownInvoice} of ${invoiceTotal} in the last 12 months`
                                : `${invoiceTotal} in the last 12 months`}
                        />
                        <CardContent>
                            {failedPage !== null && (
                                <LoadFailureAlert
                                    severity="warning"
                                    action={
                                        <Button color="inherit" size="small" onClick={retryLoad} disabled={loading}>
                                            {loading ? 'Loading...' : 'Try again'}
                                        </Button>
                                    }
                                >
                                    {failedPage === loadedPage
                                        ? 'We could not refresh this page of invoices. The figures below are the ones we loaded last.'
                                        : `We could not load page ${failedPage} of the invoice list. The invoices below are still page ${loadedPage}.`}
                                </LoadFailureAlert>
                            )}
                            {loading ? (
                                <Skeleton height={220} />
                            ) : invoices.length === 0 ? (
                                <Typography variant="body2" color="text.secondary">No invoices in the last 12 months.</Typography>
                            ) : (
                                <>
                                    <TableContainer component={Paper} variant="outlined">
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Invoice #</TableCell>
                                                    <TableCell>Date</TableCell>
                                                    <TableCell>Period</TableCell>
                                                    <TableCell>Account</TableCell>
                                                    <TableCell align="right">Amount</TableCell>
                                                    <TableCell align="right">Paid</TableCell>
                                                    <TableCell>Status</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {invoices.map(inv => (
                                                    <ClickableTableRow key={inv._id} hover onClick={() => navigate(invoiceLinkPath(inv._id))}>
                                                        <TableCell>{inv.invoiceNumber}</TableCell>
                                                        <TableCell>{formatDate(inv.invoiceDate)}</TableCell>
                                                        <TableCell>
                                                            {inv.billingPeriodStart ? `${formatDate(inv.billingPeriodStart)} – ${formatDate(inv.billingPeriodEnd)}` : '—'}
                                                        </TableCell>
                                                        <TableCell>{inv.accountName || '—'}</TableCell>
                                                        <TableCell align="right">{formatCurrency(inv.amount)}</TableCell>
                                                        <TableCell align="right">{formatCurrency(inv.paidAmount)}</TableCell>
                                                        <TableCell><Chip label={inv.status} color={statusColor(inv.status)} size="small" /></TableCell>
                                                    </ClickableTableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                    {invoicePageCount > 1 && (
                                        <PaginationRow>
                                            <Pagination
                                                count={invoicePageCount}
                                                page={page}
                                                onChange={(event, value) => setPage(value)}
                                                color="primary"
                                            />
                                        </PaginationRow>
                                    )}
                                </>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </ContainerBox>
    );
}
